/**
 * AURELIA — component-recently-viewed.js
 * <recently-viewed>
 *
 * Two jobs, deliberately in one file because they share one storage schema:
 *
 *   1. Record — on a product page, push the current product to the front of
 *      the visitor's history.
 *   2. Render — fetch the remembered products as one rendered section and
 *      swap them in.
 *
 * Storage is localStorage, keyed per shop so a shared browser cannot leak one
 * store's history into another — the same convention as the wishlist.
 *
 * Nothing here is a tracking mechanism: the list never leaves the device
 * except as product ids in a search query the visitor's own browser makes.
 */
(function () {
  'use strict';

  const { Element, fetchSection } = window.Aurelia;

  const SHOP = (window.Shopify && window.Shopify.shop) || 'shop';
  const STORAGE_KEY = `aurelia:recently-viewed:${SHOP}`;
  const LIMIT = 20;

  function read() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((id) => Number.isFinite(id)) : [];
    } catch (error) {
      // Private browsing in older Safari throws on access rather than
      // returning null. An empty history is the correct degradation.
      return [];
    }
  }

  function write(ids) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(0, LIMIT)));
    } catch (error) {
      /* Quota exceeded or storage disabled — the section simply stays hidden. */
    }
  }

  function record(id) {
    const numeric = Number(id);
    if (!Number.isFinite(numeric)) return;
    const ids = read().filter((existing) => existing !== numeric);
    ids.unshift(numeric);
    write(ids);
  }

  class RecentlyViewed extends Element {
    mount() {
      this.current = Number(this.dataset.currentProduct) || null;
      this.limit = Number(this.dataset.limit) || 4;

      // Already rendered by the search request that produced this markup —
      // this instance is the *result*, not the shell, so it must not re-fetch.
      if (this.querySelector('.product-grid, .carousel')) {
        this.hidden = false;
        return;
      }

      const history = read().filter((id) => id !== this.current);

      // Record after reading, so the product currently being viewed never
      // appears in its own "recently viewed" row.
      if (this.current) record(this.current);

      if (history.length === 0) return;

      this.load(history.slice(0, this.limit));
    }

    unmount() {
      if (this.controller) this.controller.abort();
    }

    async load(ids) {
      // `id:` is a supported search field, so one request returns every
      // remembered product already rendered as this section.
      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const url = new URL('/search', window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', query);

      this.controller = new AbortController();

      try {
        const html = await fetchSection(url.toString(), {
          sectionId: this.dataset.sectionId,
          signal: this.controller.signal,
        });

        const incoming = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('recently-viewed');

        // Every remembered product has since been unpublished or deleted.
        if (!incoming || !incoming.querySelector('.product-grid, .carousel')) return;

        this.innerHTML = incoming.innerHTML;
        this.hidden = false;
      } catch (error) {
        if (error.name === 'AbortError') return;
        // A history row is entirely optional. Failing to load it is not
        // something to interrupt a shopper about.
        console.warn('[Aurelia] recently viewed failed to load', error);
      }
    }
  }

  window.Aurelia.define('recently-viewed', RecentlyViewed);
})();
